import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X, Upload, Clipboard, Loader2 } from 'lucide-react';
import { apiClient } from '../../lib/apiClient';
import { resolveMediaUrl } from '../../lib/mediaUrl';

/**
 * Image set editor with clipboard paste.
 *
 * Built for the showcase directory: take a screenshot, press Ctrl/Cmd+V while
 * this panel is focused, and it uploads straight to storage — no save-to-disk,
 * no file dialog. Drag-drop and click-to-browse also work, because people
 * expect them and they cost nothing.
 *
 * Uploads go through apiClient, which already attaches the bearer token, so
 * auth is not re-implemented here.
 *
 * Props:
 *   value     array of image URLs (controlled)
 *   onChange  (urls: string[]) => void
 *   folder    storage folder, default "showcase"
 *   max       maximum images, default 5
 */

export const MAX_SHOWCASE_IMAGES = 5;

const isImageFile = (f) => f && f.type && f.type.startsWith('image/');

const PasteImageUpload = ({
  value = [],
  onChange,
  folder = 'showcase',
  max = MAX_SHOWCASE_IMAGES,
  label = 'Screenshots',
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const zoneRef = useRef(null);
  const inputRef = useRef(null);

  const remaining = Math.max(0, max - value.length);

  const uploadFiles = useCallback(
    async (files) => {
      const images = Array.from(files || []).filter(isImageFile);
      if (images.length === 0) return;

      if (remaining === 0) {
        setError(`Maximum ${max} images. Remove one first.`);
        return;
      }

      // Take only what fits, and say so rather than silently dropping the rest.
      const accepted = images.slice(0, remaining);
      const skipped = images.length - accepted.length;

      setBusy(true);
      setError(skipped > 0 ? `Only ${accepted.length} added — limit is ${max}.` : null);

      try {
        const uploaded = [];
        for (const file of accepted) {
          const form = new FormData();
          // A pasted screenshot has no filename, so give it one. Without this
          // the backend receives "blob" and every upload collides in the UI.
          const named =
            file.name && file.name !== 'blob'
              ? file
              : new File([file], `screenshot-${Date.now()}.png`, {
                  type: file.type || 'image/png',
                });

          form.append('file', named);
          form.append('folder', folder);

          const res = await apiClient.post('/storage/upload', form);
          if (res?.data?.url) uploaded.push(res.data.url);
        }

        if (uploaded.length) onChange([...value, ...uploaded]);
      } catch (err) {
        setError(
          err?.response?.data?.detail || 'Upload failed. Check storage settings and try again.',
        );
      } finally {
        setBusy(false);
      }
    },
    [value, onChange, folder, remaining, max],
  );

  // Paste is scoped to this panel rather than the window: an admin page can
  // hold several of these, and a window listener would send the screenshot to
  // all of them at once.
  useEffect(() => {
    const node = zoneRef.current;
    if (!node) return undefined;

    const onPaste = (e) => {
      const files = e.clipboardData?.files;
      if (files && files.length) {
        e.preventDefault();
        uploadFiles(files);
      }
    };

    node.addEventListener('paste', onPaste);
    return () => node.removeEventListener('paste', onPaste);
  }, [uploadFiles]);

  const removeAt = (index) => {
    setError(null);
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <label className="text-gray-900 text-sm font-semibold">{label}</label>
        <span className="text-gray-500 text-xs">
          {value.length} / {max}
        </span>
      </div>

      {/* tabIndex makes the panel focusable so paste targets it */}
      <div
        ref={zoneRef}
        tabIndex={0}
        role="group"
        aria-label={`${label}. Click here then paste a screenshot, or drop a file.`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          uploadFiles(e.dataTransfer?.files);
        }}
        className={`rounded-xl border-2 border-dashed p-4 transition-colors outline-none focus:border-[#FF6B00] ${
          dragOver ? 'border-[#FF6B00] bg-[#FF6B00]/5' : 'border-gray-200 bg-gray-50'
        }`}
        data-testid="paste-image-upload"
      >
        {value.length > 0 && (
          <div className="grid grid-cols-5 gap-2 mb-3">
            {value.map((url, i) => (
              <div key={`${url}-${i}`} className="relative group">
                {/* Stored paths are relative; resolve against the API origin
                    or the image 404s against the dev server. */}
                <img
                  src={resolveMediaUrl(url)}
                  alt={`${label} ${i + 1}`}
                  className="w-full h-16 object-cover rounded border border-gray-200"
                />
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  aria-label={`Remove image ${i + 1}`}
                  className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full p-0.5 text-gray-500 hover:text-gray-900 hover:border-[#FF6B00]"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="text-center py-2">
          {busy ? (
            <div className="flex items-center justify-center gap-2 text-[#FF6B00] text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Uploading…
            </div>
          ) : remaining === 0 ? (
            <p className="text-gray-500 text-sm">
              Limit reached. Remove an image to add another.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-center gap-2 text-gray-700 text-sm mb-1">
                <Clipboard className="w-4 h-4 text-[#FF6B00]" />
                Click here, then paste a screenshot
              </div>
              <p className="text-gray-500 text-xs mb-3">
                or drop an image &middot; {remaining} slot{remaining === 1 ? '' : 's'} left
              </p>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-gray-700 text-xs hover:border-[#FF6B00] hover:text-[#FF6B00] transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Browse
              </button>
            </>
          )}
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = ''; // allow re-picking the same file
          }}
        />
      </div>

      {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
    </div>
  );
};

export default PasteImageUpload;
